import type { ReactNode } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { adminEntryApplyRoleLandingState } from "@/config";
import { Spinner } from "@/components/ui/spinner";
import { useAuth } from "@/contexts/AuthContext";

type GuestGuardProps = {
  children: ReactNode;
};

/**
 * Wraps public auth pages (/signin, /signup) that sit outside AuthGuard.
 * Redirects authenticated users to the app so they never see the login form.
 *
 * Uses AuthContext as the single source of truth — avoids the race condition
 * where a separate getSession() call could return a stale cached session
 * after logout, causing a redirect loop.
 *
 * `/signin?switch=1` bypasses the redirect so staff can sign in as another
 * user without clearing site data; submitting replaces the active session.
 */
export function GuestGuard({ children }: GuestGuardProps) {
  const [searchParams] = useSearchParams();
  const switchAccount = searchParams.get("switch") === "1";
  const { status } = useAuth();

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Spinner className="text-muted-foreground" />
        <span className="sr-only">Loading session</span>
      </div>
    );
  }

  if (status === "authenticated" && !switchAccount) {
    return (
      <Navigate to="/admin" replace state={adminEntryApplyRoleLandingState} />
    );
  }

  return <>{children}</>;
}
